import type { TokenPair } from '@tasks/contracts/auth';
import { hostMode } from '#lib/host/mode';
import { refresh as callRefresh } from './auth-api.js';
import { RefreshCoordinator, type RefreshOutcome } from './refresh-coordinator.js';
import { createTabChannel, type TabChannel } from './tab-channel.js';
import { TokenStore } from './token-store.js';
import { vittoSession } from './vitto-session.js';
import { withRefreshLock } from './with-lock.js';

/** Un minuto antes de caducar: da margen para reintentar sin que ninguna petición falle. */
const RENEW_MARGIN_MS = 60_000;
export const BOOT_TIMEOUT_MS = 5000;

export type SessionState = 'unknown' | 'authenticated' | 'anonymous' | 'unreachable';

/**
 * Dueña de la sesión en el cliente. Vive fuera de React: el árbol se remonta, la sesión no.
 */
export class AuthSession {
  readonly store: TokenStore;
  private readonly channel: TabChannel;
  private readonly coordinator: RefreshCoordinator;
  private readonly listeners = new Set<(state: SessionState) => void>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private state: SessionState = 'unknown';
  /** Distingue "nunca entró" de "entró y perdió la sesión": lo primero va al login, lo segundo al modal. */
  private everIn = false;

  constructor(
    channel: TabChannel = createTabChannel(),
    call = callRefresh,
    // Inyectable para que los tests con temporizadores falsos no dependan de Web Locks,
    // que resuelve por la cola de tareas del navegador y los timers falsos no avanzan.
    lock: <T>(fn: () => Promise<T>) => Promise<T> = withRefreshLock,
    // an app framed by Vitto keeps its session in memory (see `hostMode`); the default is the persistent store of a normal tab
    store: TokenStore = new TokenStore(),
  ) {
    this.store = store;
    this.channel = channel;
    this.coordinator = new RefreshCoordinator({ store: this.store, channel, call, lock });
    this.channel.subscribe((message) => {
      if (message.kind === 'rotated') this.adopt(message.pair);
      else this.forget();
    });
  }

  get current(): SessionState {
    return this.state;
  }

  get wasAuthenticated(): boolean {
    return this.everIn;
  }

  subscribe(listener: (state: SessionState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  accessToken(): string | null {
    return this.store.access;
  }

  /** Arranque: sin refresh guardado es anónimo; con él se intenta rotar antes de montar nada. */
  async boot(): Promise<SessionState> {
    if (this.store.refresh === null) return this.moveTo('anonymous');
    return this.applyOutcome(await this.coordinator.run());
  }

  async renew(): Promise<SessionState> {
    return this.applyOutcome(await this.coordinator.run());
  }

  accept(pair: TokenPair): void {
    this.store.save(pair);
    this.channel.post({ kind: 'rotated', pair });
    this.schedule();
    this.moveTo('authenticated');
  }

  /** Pérdida involuntaria (revocada desde otro sitio): la UI ofrece volver a entrar en el sitio. */
  forget(): void {
    this.store.clear();
    this.stopTimer();
    this.moveTo('anonymous');
  }

  /** Salida deliberada: el usuario quiere irse, así que vuelve a la pantalla de login. */
  signOut(): void {
    this.everIn = false;
    // who a Vitto session was belongs to the session that just ended: a later password login must not inherit it (U2b-2)
    vittoSession.clear();
    this.forget();
  }

  private adopt(pair: TokenPair): void {
    this.store.adopt(pair);
    this.schedule();
    this.moveTo('authenticated');
  }

  private applyOutcome(outcome: RefreshOutcome): SessionState {
    if (outcome.status === 'refreshed' || outcome.status === 'adopted') {
      this.schedule();
      return this.moveTo('authenticated');
    }
    this.stopTimer();
    // `offline` conserva el refresh: la app reintenta en vez de pedir credenciales.
    return this.moveTo(outcome.status === 'offline' ? 'unreachable' : 'anonymous');
  }

  private schedule(): void {
    this.stopTimer();
    const expiresAt = this.store.expiresAt;
    if (expiresAt === null) return;
    const delay = Math.max(expiresAt - Date.now() - RENEW_MARGIN_MS, 1000);
    this.timer = setTimeout(() => void this.renew(), delay);
  }

  private stopTimer(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private moveTo(state: SessionState): SessionState {
    if (state === 'authenticated') this.everIn = true;
    this.state = state;
    for (const listener of this.listeners) listener(state);
    return state;
  }
}

/**
 * An app framed by Vitto keeps its session in memory and shares nothing between tabs: a refresh token in localStorage (or a rotation
 * broadcast to other tabs) would let the NEXT person who opens the app in this browser find the session of the previous one.
 */
const embedded = hostMode.kind === 'embedded';
export const authSession = new AuthSession(
  embedded
    ? { post: () => undefined, subscribe: () => () => undefined, close: () => undefined }
    : createTabChannel(),
  callRefresh,
  withRefreshLock,
  new TokenStore({ persist: !embedded }),
);
